/**
 * @file FeedMessageBubble.tsx
 * @description Пузырь сообщения в ленте (chat-window-design.md §3):
 *  - клиент — слева, подложка `--surface-low`, «хвост» 4 слева снизу;
 *  - мои — справа, цвет действия, под пузырём ✓ отправлено / ✓✓ прочитано;
 *  - коллеги — справа, подложка выбранного, над первой репликой серии имя;
 *  - пометка «Реклама» вверху пузыря; длинные сворачиваются; ссылки живые.
 * У реплики клиента — «Скопировать текст» (наведение на широком экране,
 * долгое нажатие на телефоне). В `memo`: перерисовка ленты не трогает
 * пузыри, у которых не сменилось сообщение.
 */

import { Check, CheckCheck, Copy } from "lucide-react";
import { memo, useRef } from "react";

import { Badge } from "../ui/badge";
import { useNotify } from "../ui/NotifyProvider";
import { cn } from "@/lib/utils";
import type { ChatMessageDto } from "../../lib/api-chat";
import { clockLabel } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";
import type { BubbleSide } from "./feed-model";
import { MessageText } from "./message-text";

const LONG_PRESS_MS = 550;

const SIDE_CLASS: Record<BubbleSide, { row: string; bubble: string; link: string; time: string }> = {
  customer: {
    row: "justify-start",
    bubble: "bg-surface-low text-on-surface rounded-xl rounded-bl-[4px]",
    link: "text-link underline",
    time: "text-on-surface-variant",
  },
  mine: {
    row: "justify-end",
    bubble: "bg-primary text-on-primary rounded-xl rounded-br-[4px]",
    link: "text-on-primary underline",
    time: "text-on-primary/80",
  },
  colleague: {
    row: "justify-end",
    bubble: "bg-accent text-on-surface rounded-xl rounded-br-[4px]",
    link: "text-link underline",
    time: "text-on-surface-variant",
  },
  system: {
    row: "justify-center",
    bubble: "bg-transparent text-on-surface-variant rounded-lg border border-outline-variant",
    link: "text-link underline",
    time: "text-on-surface-variant",
  },
};

interface Props {
  message: ChatMessageDto;
  side: BubbleSide;
  showAuthor: boolean;
  /** В развёрнутом окне пузырь до 560, иначе до 75% ширины. */
  wide: boolean;
}

function copy(text: string, done: () => void) {
  void navigator.clipboard?.writeText(text).then(done).catch(() => undefined);
}

export const FeedMessageBubble = memo(function FeedMessageBubble({ message, side, showAuthor, wide }: Props) {
  const notify = useNotify();
  const look = SIDE_CLASS[side];
  const pressTimer = useRef(0);
  const copyText = () => copy(message.body, () => notify.success(CHAT_TEXT.copied));
  const customer = side === "customer";

  return (
    <div data-chat-message={message.id} className={cn("group flex flex-col", side === "customer" ? "items-start" : side === "system" ? "items-center" : "items-end")}>
      {showAuthor && message.author ? <span className="mb-0.5 px-1 text-xs font-medium text-on-surface-variant">{message.author.name}</span> : null}
      <div className={cn("flex w-full items-end gap-1", look.row)}>
        <div
          className={cn("min-w-0 px-3 py-2 text-sm", look.bubble, wide ? "max-w-[min(75%,560px)]" : "max-w-[75%]")}
          onPointerDown={customer ? () => (pressTimer.current = window.setTimeout(copyText, LONG_PRESS_MS)) : undefined}
          onPointerUp={customer ? () => window.clearTimeout(pressTimer.current) : undefined}
          onPointerLeave={customer ? () => window.clearTimeout(pressTimer.current) : undefined}
        >
          {message.isMarketing ? (
            <Badge variant="warning" className="mb-1">
              {CHAT_TEXT.advertising}
            </Badge>
          ) : null}
          <MessageText body={message.body} linkClass={look.link} />
          <span className={cn("ml-2 inline-flex translate-y-0.5 items-center gap-1 align-bottom text-xs tabular-nums", look.time)}>
            {clockLabel(message.createdAt)}
            {side === "mine" ? (
              message.readByCustomerAt ? (
                <CheckCheck className="size-3.5" aria-label={CHAT_TEXT.readByCustomer} />
              ) : (
                <Check className="size-3.5" aria-label={CHAT_TEXT.sent} />
              )
            ) : null}
          </span>
        </div>
        {customer ? (
          <button
            type="button"
            aria-label={CHAT_TEXT.copyText}
            title={CHAT_TEXT.copyText}
            onClick={copyText}
            className="hidden size-8 shrink-0 items-center justify-center rounded-md text-on-surface-variant opacity-0 outline-none transition-opacity hover:bg-surface-low focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100 lg:inline-flex"
          >
            <Copy className="size-4" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
});
